/**
 * A vendor SDK that is downloaded the first time it is needed, so a tenant
 * that has not configured the vendor never pays for it. Calls made while the
 * import is in flight wait and run in order once it resolves; nothing sent
 * right after `load()` is lost. If the import fails, the waiting calls are
 * dropped and later calls do nothing.
 */
export interface LazySdk<T> {
	/** Starts the download (once) and runs `setup` on the loaded SDK. */
	load: () => void;
	/** Runs `call` now if loaded, later if loading, never if `load` was not called. */
	use: (call: (sdk: T) => void) => void;
}

export function lazySdk<T>(
	importSdk: () => Promise<T>,
	setup?: (sdk: T) => void,
): LazySdk<T> {
	let sdk: T | undefined;
	let loading = false;
	let pending: Array<(sdk: T) => void> = [];

	return {
		load: () => {
			if (sdk !== undefined || loading) {
				return;
			}
			loading = true;
			void importSdk()
				.then((loaded) => {
					setup?.(loaded);
					sdk = loaded;
					const queued = pending;
					pending = [];
					queued.forEach((call) => call(loaded));
				})
				.catch(() => {
					pending = [];
				})
				.finally(() => {
					loading = false;
				});
		},
		use: (call) => {
			if (sdk !== undefined) {
				call(sdk);
			} else if (loading) {
				pending.push(call);
			}
		},
	};
}

/** `/issue/spring` → the full URL a vendor's page-view call expects. */
export function urlOf(path: string): string {
	return new URL(path, window.location.origin).href;
}
